import React, { useCallback, useEffect, useMemo, useRef } from "react";

import {
  faClone,
  faCopy,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import {
  getSchemaTypeIcon,
  isBaseSchemaType,
} from "src/base-components/SchemaTypeSelector";
import {
  CellDisplayType,
  ColumnsConfig,
  getAllKeys,
  getColumnDisplayName,
  getColumnDisplayType,
  isColumnEditable,
  resolveColumnType,
  TABLE_COLUMN_MIN_WIDTH,
  TABLE_COLUMN_WIDTH,
} from "src/cases/caseView/blocks/cellTypes";
import { EditableTableCell } from "src/cases/caseView/blocks/EditableTableCell";
import { useEditableTableKeyboard } from "src/cases/caseView/blocks/useEditableTableKeyboard";
import {
  EditableTableSelectionContext,
  useCreateEditableTableSelectionStore,
} from "src/cases/caseView/blocks/useEditableTableSelection";
import { UseTableBlockEditsReturn } from "src/cases/caseView/blocks/useTableBlockEdits";
import { useIsBlockExpanded } from "src/cases/caseView/CaseBlock";
import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";
import { TableEditMode } from "src/clients/case-management";
import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { copyTextToClipboard } from "src/utils/clipboard";

// Same column sizing as MultiRecordDisplay so mode switches don't reflow.
const COLUMN_STYLE = {
  width: TABLE_COLUMN_WIDTH,
  minWidth: TABLE_COLUMN_MIN_WIDTH,
  maxWidth: TABLE_COLUMN_WIDTH,
} as const;

type EditableTableBlockProps = {
  records: TableBlockMultiColumnRecord;
  columnsConfig: ColumnsConfig;
  defaultColumnsEditMode?: TableEditMode;
  edits: UseTableBlockEditsReturn;
};

const helper = createColumnHelper<Record<string, unknown>>();

export const EditableTableBlock: React.FC<EditableTableBlockProps> = ({
  records,
  columnsConfig,
  defaultColumnsEditMode,
  edits,
}) => {
  const isExpanded = useIsBlockExpanded();
  const {
    editedData,
    isCellModified,
    updateCell,
    addRow,
    deleteRow,
    duplicateRow,
    isNewRow,
  } = edits;

  const store = useCreateEditableTableSelectionStore();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const columnKeys = useMemo(() => getAllKeys(records), [records]);

  const editableColumnKeys = useMemo(
    () =>
      columnKeys.filter((key) =>
        isColumnEditable(key, columnsConfig, defaultColumnsEditMode),
      ),
    [columnKeys, columnsConfig, defaultColumnsEditMode],
  );

  const columnDisplayTypes = useMemo(
    () =>
      Object.fromEntries(
        columnKeys
          .filter((key) => columnsConfig?.[key]?.display_config?.type != null)
          .map((key) => [
            key,
            resolveColumnType(columnsConfig![key]!.display_config!, undefined),
          ]),
      ) as Record<string, CellDisplayType>,
    [columnKeys, columnsConfig],
  );

  // Current values (row-aligned for the type filter) plus the pre-edit
  // snapshot, so enum/tags options keep replaced values until save/cancel.
  const columnValuesMap = useMemo(
    () =>
      Object.fromEntries(
        columnKeys.map((key) => [
          key,
          [
            ...editedData.map((row) => row[key]),
            ...records.map((row) => row[key]),
          ],
        ]),
      ) as Record<string, unknown[]>,
    [columnKeys, editedData, records],
  );

  // The memoized column defs and keyboard handler read current values
  // through this single ref bag instead of one mirrored ref per value.
  const latestValues = {
    editedData,
    isCellModified,
    columnKeys,
    editableColumnKeys,
    columnDisplayTypes,
    columnValuesMap,
    isNewRow,
    getTypeOverride: edits.getTypeOverride,
    setTypeOverride: edits.setTypeOverride,
    getCellHeight: edits.getCellHeight,
    setCellHeight: edits.setCellHeight,
  };
  const latest = useRef(latestValues);
  latest.current = latestValues;

  useEffect(() => {
    const unsub = store.subscribe((state, prev) => {
      if (state.selectedCell === prev.selectedCell) return;
      if (state.selectedCell && !state.selectedCell.editing) {
        requestAnimationFrame(() => {
          const current = store.getState().selectedCell;
          if (!current || current.editing) return;
          if (
            tableContainerRef.current &&
            !tableContainerRef.current.contains(document.activeElement)
          ) {
            tableContainerRef.current.focus();
          }
        });
      }
    });
    return unsub;
  }, [store]);

  const handleKeyDown = useEditableTableKeyboard({
    store,
    tableContainerRef,
    latest,
    updateCell,
  });

  const columns = useMemo(() => {
    return columnKeys.map((key) =>
      helper.accessor((row) => row[key], {
        id: key,
        header: () => {
          const displayName = getColumnDisplayName(key, columnsConfig);
          const displayType = getColumnDisplayType(key, columnsConfig);
          const schemaType =
            displayType === "currency" ? "number" : displayType;
          const typeIcon =
            schemaType && isBaseSchemaType(schemaType)
              ? getSchemaTypeIcon(schemaType)
              : null;

          return (
            <div className="flex h-8 items-center gap-1.5 pr-2 pl-1.5 font-inter-medium-12px text-gray-800">
              {typeIcon && <Icon icon={typeIcon} size="2xs" />}
              <span className="truncate">{displayName}</span>
            </div>
          );
        },
        cell: (info) => {
          const declaredType = latest.current.columnDisplayTypes[key];
          const cellDisplayType =
            declaredType ?? resolveColumnType(null, info.getValue());
          const cellType = (
            latest.current.getTypeOverride(info.row.index, key) ??
            cellDisplayType
          )?.type;

          const allValues = latest.current.columnValuesMap[key];
          const filteredValues = allValues?.filter((_, rowIdx) => {
            const rowDisplayType =
              declaredType ?? resolveColumnType(null, allValues[rowIdx]);
            const rowType = (
              latest.current.getTypeOverride(rowIdx, key) ?? rowDisplayType
            )?.type;
            return rowType === cellType;
          });

          return (
            <EditableTableCell
              columnKey={key}
              columnValues={filteredValues}
              displayType={cellDisplayType}
              hasDeclaredType={declaredType != null}
              isEditable={
                isColumnEditable(key, columnsConfig, defaultColumnsEditMode) ||
                latest.current.isNewRow(info.row.index)
              }
              isEdited={latest.current.isCellModified(info.row.index, key)}
              rowIndex={info.row.index}
              typeOverride={latest.current.getTypeOverride(info.row.index, key)}
              userHeight={latest.current.getCellHeight(info.row.index, key)}
              value={info.getValue()}
              onChange={(newValue) => updateCell(info.row.index, key, newValue)}
              onResize={(height) =>
                latest.current.setCellHeight(info.row.index, key, height)
              }
              onTypeOverride={(newType) =>
                latest.current.setTypeOverride(info.row.index, key, newType)
              }
            />
          );
        },
      }),
    );
  }, [
    columnKeys,
    columnsConfig,
    defaultColumnsEditMode,
    updateCell,
    columnDisplayTypes,
  ]);

  const table = useReactTable({
    data: editedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_, index) => String(index),
  });

  const handleDuplicateRow = useCallback(
    (rowIndex: number) => {
      duplicateRow(rowIndex);
      store.getState().actions.adjustForRowInsert(rowIndex);
    },
    [duplicateRow, store],
  );

  const handleCopyRow = useCallback(
    (rowIndex: number) =>
      copyTextToClipboard(
        JSON.stringify(latest.current.editedData[rowIndex], null, 2),
      ),
    [],
  );

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      deleteRow(rowIndex);
      store.getState().actions.adjustForRowDelete(rowIndex);
    },
    [deleteRow, store],
  );

  const hasReadOnlyColumns = editableColumnKeys.length < columnKeys.length;

  const rowActionElements = useCallback(
    (rowIndex: number): OptionsDropdownElement[] => [
      {
        key: "Duplicate",
        action: () => handleDuplicateRow(rowIndex),
        icon: faClone,
      },
      {
        key: "Copy payload as JSON",
        action: () => handleCopyRow(rowIndex),
        icon: faCopy,
      },
      {
        key: "Delete",
        action: () => handleDeleteRow(rowIndex),
        icon: faTrashCan,
        disabled:
          hasReadOnlyColumns && !isNewRow(rowIndex)
            ? "Cannot delete rows in a table with read-only columns"
            : false,
      },
    ],
    [
      handleDuplicateRow,
      handleCopyRow,
      handleDeleteRow,
      hasReadOnlyColumns,
      isNewRow,
    ],
  );

  const addRowButton = (
    <Button
      dataLoc="editable-table-add-row"
      iconLeft={faPlus}
      size="sm"
      variant="secondary"
      onClick={addRow}
    >
      Add row
    </Button>
  );

  return (
    <EditableTableSelectionContext value={store}>
      <div className="flex h-full flex-col">
        <div
          ref={tableContainerRef}
          className={
            isExpanded
              ? "decideScrollbar min-h-0 flex-1 overflow-auto pt-4 pr-5 pb-8 pl-5 outline-none"
              : "decideScrollbar min-h-0 flex-1 overflow-auto p-4 outline-none"
          }
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <table className="w-full table-auto border-collapse border-spacing-0">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-gray-100 border-b bg-gray-50"
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="relative border-gray-100 border-r p-0 text-left first:border-l"
                      style={COLUMN_STYLE}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="group/row border-gray-100 border-b">
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <td
                      key={cell.id}
                      className="group/table-cell relative border-gray-100 border-r p-0 align-top first:border-l"
                      style={COLUMN_STYLE}
                    >
                      {cellIndex === 0 && (
                        <div className="absolute top-1.5 -left-2 z-10 flex h-5 w-[15px] items-center justify-center rounded border border-gray-200 bg-white opacity-0 group-hover/row:opacity-100">
                          <EllipsisOptionsDropdown
                            dataLoc={`editable-table-row-actions-${row.index}`}
                            elements={rowActionElements(row.index)}
                            iconSize="2xs"
                          />
                        </div>
                      )}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!isExpanded && <div className="pt-3">{addRowButton}</div>}
        </div>
        {isExpanded && (
          <div className="border-gray-200 border-t p-4">{addRowButton}</div>
        )}
      </div>
    </EditableTableSelectionContext>
  );
};
